import { createAuth0Client, Auth0Client } from "@auth0/auth0-spa-js";
import { BaseQueryFn } from "@reduxjs/toolkit/query";
import axios, { AxiosRequestConfig, AxiosError } from "axios";
import { ORG_HEADER, activeOrgHeader } from "../utils/activeOrg";

export const auth0Config = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN || "",
  clientId: process.env.REACT_APP_AUTH0_CLIENT_ID || "",
  authorizationParams: {
    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    redirect_uri: window.location.origin,
  },
  cacheLocation: "localstorage" as const,
  onRedirectCallback: (appState?: any) => {
    window.history.replaceState(
      {},
      document.title,
      appState?.returnTo || window.location.pathname,
    );
  },
};

let client: Auth0Client;

const getAuthClient = async () => {
  if (!client) {
    client = await createAuth0Client(auth0Config);
  }
  return client;
};

interface CustomArgs extends AxiosRequestConfig {
  devPort?: string;
  // This allows us to pass the token from the component call if needed,
  baseUrl?: string;
  /**
   * Act as this organization for THIS request only, instead of the one the user
   * currently has selected.
   *
   * For the superuser panel, which shows another organization's data without
   * moving the operator's own context. The backend already accepts the
   * organization per request and already permits a superuser to name any one, so
   * nothing server-side changes.
   *
   * Belongs in the query ARG rather than being read from a global, so it lands
   * in RTK Query's cache key: two organizations viewed in one session must not
   * share a cache entry, or the panel poisons the operator's own data.
   */
  org?: string;
}

const getBaseURL = (devPort?: string) => {
  const protocol = process.env.REACT_APP_ENV !== "dev" ? "https://" : "http://";
  const endpoint = process.env.REACT_APP_BACKEND_ENDPOINT || "";
  const base = protocol + endpoint;

  if (devPort && process.env.REACT_APP_ENV === "dev") {
    return base.slice(0, -4) + devPort;
  }
  return base;
};

export const axiosBaseQuery =
  (): BaseQueryFn<CustomArgs, unknown, unknown> =>
  async ({ url, method, data, params, headers, devPort, org }) => {
    try {
      const auth0 = await getAuthClient();
      const token = await auth0.getTokenSilently();

      const result = await axios({
        url: getBaseURL(devPort) + url,
        method,
        data,
        params,
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          // An explicit org wins for this one request; otherwise the user's own
          // selection, read at call time so switching takes effect on the next
          // request. Omitted entirely when neither is set, which is how this
          // stays a no-op for single-org users.
          ...(org ? { [ORG_HEADER]: org } : activeOrgHeader()),
        },
      });
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };
